import React, { FC } from "react";
import { UserSelectionSetInput } from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import UserSelectionPreview from "./UserSelectionPreview";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import PlusIcon from "@src/lib/icons/18px/plus";
import DotsIcon from "@src/lib/icons/18px/dots";

type Props = {
  input: UserSelectionSetInput;
  showUserTypeOptions: boolean;
  onEdit?: (index: number) => void;
  onRemove?: (index: number) => void;
  onAdd?: () => void;
  disabled?: boolean;
  showCounts?: boolean;
  /** Replaces the default per-segment "N users currently belong" line. */
  renderCountText?: (count: number) => React.ReactNode;
  showAdd?: boolean;
  showRemove?: boolean;
  color?: string;
  noun?: string;
  emptyStateClassName?: string;
  emptyStateTextClassName?: string;
  firstSelectionClassName?: string;
  chatId?: string;
};

const UserSelectionSetPreview: FC<Props> = ({
  input,
  onEdit,
  onRemove,
  onAdd,
  color = "purple",
  disabled = false,
  showCounts = false,
  renderCountText,
  showAdd = true,
  showRemove = true,
  noun = "users",
  emptyStateClassName,
  emptyStateTextClassName = "text-gray-9",
  firstSelectionClassName,
  chatId,
}) => {
  if (input.userSelections.length === 0) {
    return (
      <div
        className={cn(
          "flex h-[48px] items-center justify-between rounded-xl bg-gray-1 pr-[6px] pl-[16px]",
          emptyStateClassName,
        )}
      >
        <Text
          variant="fine-print-description"
          className={emptyStateTextClassName}
        >
          0 {noun} selected
        </Text>
        <Button
          onClick={onAdd}
          disabled={disabled}
          data-testid="select-users-button"
        >
          Select {noun}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {input.userSelections.map((selection, index) => {
        const selectionClassName =
          index === 0 ? firstSelectionClassName : undefined;
        return (
          <div
            key={JSON.stringify(selection)}
            className={cn(
              "[container-type:inline-size] relative overflow-hidden rounded-xl",
              selectionClassName,
            )}
          >
            <UserSelectionPreview
              pillColor={color}
              input={selection}
              showUserTypeOptions={true}
              showCards={true}
              showCounts={showCounts}
              renderCountText={renderCountText}
              countClassName={
                disabled ? undefined : "[@container(max-width:475px)]:pr-[40px]"
              }
              chatId={chatId}
              className={cn("p-2", selectionClassName)}
            />
            {!disabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-[4px] right-[4px] [@container(max-width:475px)]:top-auto [@container(max-width:475px)]:bottom-[4px]"
                  >
                    <DotsIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => onEdit?.(index)}>
                    <PenOutlineIcon className="mr-2 size-4" />
                    Edit
                  </DropdownMenuItem>
                  {showRemove && (
                    <DropdownMenuItem onClick={() => onRemove?.(index)}>
                      <TrashOutlineIcon className="mr-2 size-4" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      })}
      {!disabled && showAdd && (
        <div className="flex items-center">
          <Button
            variant="outline"
            size="md"
            onClick={onAdd}
            data-testid="select-users-button"
          >
            <PlusIcon />
            Add a segment
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserSelectionSetPreview;
